/**
 * @module ui.store
 * @version 5.3.1
 * @description LIFTR Store Web Component - Opening Hours + Distance Display
 *
 * Features:
 * - Opening Hours: Status (open/closed/closing/opening), Labels, Registry
 * - Distance: Renders distance/travel/maps from liftr:location:resolved events
 * - Directions Link: Sets Google Maps directions href on distance resolve
 * - Directions Fallback: Restores Liquid-rendered href (Place ID or address) on reset
 * - Native List Filter Integration (liftr-field + liftr-value)
 * - Schedule Table: Active weekday row via liftr-state="active" (per minute)
 *
 * Distance input (PLZ + Geolocation) is handled by liftr-location.
 * This module is a pure consumer of liftr:location:resolved events.
 *
 * CHANGELOG:
 * - 5.3.1: CLEANUP - Removed the dead store-side holiday override read. The
 *          store-data payload no longer ships schedule.holiday (the
 *          opening_hours_holiday metaobject field is functionally dead — Google
 *          opening_hours is the sole source for hours and holidays). No longer
 *          builds holidayBySlug/holidaySlugByISO nor passes them to
 *          createOpeningCore. Pure cleanup, no behavior change: the field always
 *          resolved to null and the Google path already ignored the legacy
 *          slug mechanism. The market-global legacy path in the core is unaffected.
 * - 5.3.0: CHANGE - Schedule table is now rendered by JS (span model, api.live
 *          v4.3.0). #renderScheduleWeek() fills the 7 rows on mount from
 *          formatter.week()/core.displayWeek() — the Liquid snippet only ships
 *          the static skeleton (empty times containers). DOM structure, classes,
 *          liftr-role and liftr-value stay byte-identical to the former SSR
 *          output → snippet.store.css untouched. #renderScheduleDay() no longer
 *          re-parses "HH:MM|HH:MM" from the DOM; slot-active is derived from
 *          core.spansForDate(today, { includeCarry:false }) in today's
 *          coordinates. Over-midnight windows therefore stay attached to their
 *          opening day (a running carry marks the row active but no slot).
 * - 5.2.0: NEW - Directions Fallback: On mount, reads Liquid-rendered href
 *          from each store-directions-link element. On reset/neutralize,
 *          restores fallback href (Google Place ID or address) instead of
 *          removing href entirely. Ensures directions link is always usable.
 *          Extracted #restoreDirectionsFallbacks() helper to DRY reset/neutralize.
 * - 5.1.1: FIX - store-directions-link: role() → roleAll() to support
 *          multiple directions links per store instance.
 * - 5.1.0: NEW - store-directions-link role: sets Google Maps Directions href
 *          on distance resolve with travelmode based on distance category
 *          (near=walking, mid=bicycling, far/remote=driving).
 *          Uses resolved user coords as origin (not device GPS).
 * - 5.0.0: BREAKING - Extracted postal/geo input to ui.location.js
 *          Removed: GLOBAL_ROLES, storage helpers, input listeners, geo handler
 *          Removed: #inputEl, #deleteBtn, #geoIndex, #elsPostalDisplay
 *          Removed: #computeAndRenderDistance, #handleLocationSet, #updatePostalDisplay
 *          Removed: refreshDistance() public API
 *          Added: LISTEN.LOCATION_RESOLVED for liftr:location:resolved
 *          Added: #onLocationResolved() as single entry point for distance
 *          Changed: import — removed debounce, ensureLive, roleAllGlobal
 *          Changed: import — added on from core.liftr.js
 * - 4.4.0: NEW - Schedule Table Active Day
 * - 4.3.0: v4.5+ COMPLIANCE
 * - 4.2.0: BREAKING - Removed Finsweet legacy support
 * - 4.1.2: NEW - distance-unset state
 * - 4.1.0: BREAKING - Removed redundant states
 * - 4.0.0: Initial v4.0 implementation
 *
 * @element liftr-store
 *
 * @fires liftr:store:opening-changed - Opening status changed
 * @fires liftr:store:distance-changed - Distance calculated/cleared
 *
 * @attr {string} liftr-id - Store identifier (required)
 * @attr {string} liftr-state - Unified state output:
 *   Opening: open, closed, closing, opening (can be combined: "open closing")
 *   Distance: distance-unset, distance-unknown, distance-near, distance-mid, distance-far, distance-remote
 *
 * @css-selectors
 * liftr-store[liftr-state~="open"] { }
 * liftr-store[liftr-state~="closed"] { }
 * liftr-store[liftr-state~="closing"] { }
 * liftr-store[liftr-state~="opening"] { }
 * liftr-store[liftr-state~="distance-unset"] { }
 * liftr-store[liftr-state~="distance-unknown"] { }
 * liftr-store[liftr-state~="distance-near"] { }
 * liftr-store[liftr-state~="distance-mid"] { }
 * liftr-store[liftr-state~="distance-far"] { }
 * liftr-store[liftr-state~="distance-remote"] { }
 * [liftr-role~="store-schedule-row"][liftr-state~="active"] { }
 */

import { LiftrModule } from 'core.module';
import { ATTRIBUTES, on, i18n, settings } from 'core.liftr';
import * as live from 'api.live';
import * as geo from 'api.geo';

/* ═══════════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  // Data
  DATA: 'store-data',

  // Opening Labels
  LIVE_CURRENT: 'live-label-current',
  LIVE_NEXT:    'live-label-next',

  // Distance Labels
  LABEL_DISTANCE: 'store-label-distance',
  LABEL_DISTANCE_VALUE: 'store-label-distance-value',
  LABEL_DISTANCE_UNIT:  'store-label-distance-unit',

  // Travel Time Labels
  LABEL_WALKING:    'store-label-walking',
  LABEL_BICYCLING:  'store-label-bicycling',
  LABEL_DRIVING:    'store-label-driving',

  // Maps Links
  LINK_WALKING:     'store-link-walking',
  LINK_BICYCLING:   'store-link-bicycling',
  LINK_DRIVING:     'store-link-driving',

  // Directions Link (Google Maps directions with auto travelmode)
  LINK_DIRECTION:    'store-link-direction',

  // Schedule Table
  SCHEDULE_ROW:  'store-schedule-row',
  SCHEDULE_SLOT: 'store-schedule-slot'
});

/**
 * State tokens for liftr-state attribute.
 * Opening states are combinable (e.g. "open closing").
 * Distance states are exclusive (only one at a time).
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  // Opening (combinable)
  OPEN:    'open',
  CLOSED:  'closed',
  OPENING: 'opening',
  CLOSING: 'closing',

  // Distance (exclusive)
  DISTANCE_UNSET:   'distance-unset',
  DISTANCE_UNKNOWN: 'distance-unknown',
  DISTANCE_NEAR:    'distance-near',
  DISTANCE_MID:     'distance-mid',
  DISTANCE_FAR:     'distance-far',
  DISTANCE_REMOTE:  'distance-remote',

  // Schedule Row
  ACTIVE: 'active'
});

/**
 * Event action names (auto-prefixed with liftr:store:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  OPENING_CHANGED:  'opening-changed',
  DISTANCE_CHANGED: 'distance-changed'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  // Reserved
});

/**
 * External events this module listens to.
 * @type {Readonly<Record<string, string>>}
 */
const LISTEN = Object.freeze({
  LOCATION_RESOLVED: 'liftr:location:resolved'
});

/* ═══════════════════════════════════════════════════════════════════════
 * DERIVED CONSTANTS (internal, not exported)
 * ═══════════════════════════════════════════════════════════════════════ */

/** All opening state tokens (for batch removal) */
const OPENING_STATE_TOKENS = [
  STATES.OPEN, STATES.CLOSED, STATES.OPENING, STATES.CLOSING
];

/** All distance state tokens (for batch removal) */
const DISTANCE_STATE_TOKENS = [
  STATES.DISTANCE_UNSET, STATES.DISTANCE_UNKNOWN,
  STATES.DISTANCE_NEAR, STATES.DISTANCE_MID,
  STATES.DISTANCE_FAR, STATES.DISTANCE_REMOTE
];

/**
 * Maps api.geo.js category strings to STATES constants.
 * @type {Readonly<Record<string, string>>}
 */
const CATEGORY_TO_STATE = Object.freeze({
  near:   STATES.DISTANCE_NEAR,
  mid:    STATES.DISTANCE_MID,
  far:    STATES.DISTANCE_FAR,
  remote: STATES.DISTANCE_REMOTE
});

/**
 * Maps distance category to fastest Google Maps travelmode.
 * near (<3km) = walking, mid (3-7km) = bicycling, far/remote (>7km) = driving.
 * @type {Readonly<Record<string, string>>}
 */
const CATEGORY_TO_TRAVELMODE = Object.freeze({
  near:   'walking',
  mid:    'bicycling',
  far:    'driving',
  remote: 'driving'
});

/** Field names for native list filtering */
const FIELDS = Object.freeze({
  DISTANCE_KM: 'distance_km',
  DISTANCE_M: 'distance_m',
  DISTANCE_NEAR: 'distance_near',
  OPENING: 'opening'
});

const SORT_SENTINEL = 99999;

/* ═══════════════════════════════════════════════════════════════════════
 * HELPERS
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Resolves status into array of states for liftr-state.
 * "closing" -> [OPEN, CLOSING], "opening" -> [CLOSED, OPENING]
 * @param {string} status - Raw status from openingCore
 * @returns {string[]} Array of state tokens
 */
function resolveOpeningStates(status) {
  if (status === live.STATUS.CLOSING) {
    return [STATES.OPEN, STATES.CLOSING];
  }
  if (status === live.STATUS.OPENING) {
    return [STATES.CLOSED, STATES.OPENING];
  }
  return [status];
}

/**
 * Toggles a single state token on a child element without touching other tokens.
 * Used for schedule rows (not the host element, so this.state cannot be used).
 * @param {Element} el - Target element
 * @param {string} token - State token to toggle
 * @param {boolean} force - true = add, false = remove
 */
function toggleChildState(el, token, force) {
  const current = el.getAttribute(ATTRIBUTES.STATE) || '';
  const tokens = current.split(' ').filter(t => t && t !== token);
  if (force) tokens.push(token);
  const next = tokens.join(' ').trim();
  if (next !== current) {
    if (next) el.setAttribute(ATTRIBUTES.STATE, next);
    else el.removeAttribute(ATTRIBUTES.STATE);
  }
}

/**
 * Builds a Google Maps Directions URL with explicit origin and destination.
 * Origin = user location (from PLZ/geo), Destination = store location.
 * Without origin, Google Maps would use the device's GPS instead of the resolved PLZ coords.
 * @param {{ lat: number, lng: number }} origin - User coordinates (resolved PLZ or geolocation)
 * @param {{ lat: number, lng: number }} destination - Store coordinates
 * @param {string} travelmode - Google Maps travelmode (walking, bicycling, driving)
 * @returns {string} Full Google Maps Directions URL
 */
function buildDirectionsUrl(origin, destination, travelmode) {
  return `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${destination.lat},${destination.lng}&travelmode=${travelmode}`;
}

/* ═══════════════════════════════════════════════════════════════════════
 * LIFTR STORE WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════════ */

class LiftrStore extends LiftrModule {

  /* ─────────────────────────────────────────────────────────────────────
   * STATIC PROPERTIES
   * ───────────────────────────────────────────────────────────────────── */

  static NAME = 'store';

  static DEFAULTS = {
    // Opening Hours
    closingThresholdMin: 30,

    // Distance (display config — input config now in ui.location.js)
    thresholds: { near: 3, mid: 7, far: 12 },
    timeFormat: {
      style: 'short',
      roundToMin: 5,
      collapseRemainderUnder: 5,
      showDaysFromHours: 24,
      labels: { d: 'd', h: 'h', min: 'min' }
    },
    displayUnits: {
      auto: true,
      meterBelowKm: 1,
      meterRound: 10,
      kmDecimals: 0
    }
  };

  /* ─────────────────────────────────────────────────────────────────────
   * PRIVATE FIELDS
   * ───────────────────────────────────────────────────────────────────── */

  // Store Data
  #storeId = null;
  #storeCoords = null;
  #schedule = null;

  // Opening Core
  #dateFx = null;
  #openingCore = null;
  #openingFormatter = null;
  #closingThreshold = 30;

  // Elements - Opening
  #elsOpeningLabel = [];
  #elsOpeningNext = [];

  // Elements - Distance
  #elsLabelDistance = [];
  #elsLabelUnit = [];
  #elsLabelWalking = [];
  #elsLabelBicycling = [];
  #elsLabelDriving = [];
  #elLinkWalking = null;
  #elLinkBicycling = null;
  #elLinkDriving = null;
  #elsDirectionsLink = [];
  #directionsHrefFallbacks = [];

  // Elements - Schedule Table
  #elsScheduleRows = [];

  // Elements - List Filter Fields
  #elFieldDistanceKm = null;
  #elFieldDistanceM = null;
  #elFieldDistanceNear = null;
  #elFieldOpening = null;

  // Distance Config
  #thresholds = null;
  #timeFormat = null;
  #displayUnits = null;
  #marketTz = 'UTC';

  // State tracking (for change detection)
  #lastOpening = { states: [], current: null, next: null };
  #lastDistance = { label: null, category: null };

  /* ─────────────────────────────────────────────────────────────────────
   * LIFECYCLE
   * ───────────────────────────────────────────────────────────────────── */

  mount() {
    this.#readStoreData();
    this.#queryElements();
    this.#setupOpening();
    this.#setupDistance();
    this.#renderOpening();
    this.#renderScheduleWeek();
    this.#renderScheduleDay();
  }

  unmount() {
    this.#neutralizeOpening();
    this.state.remove(...DISTANCE_STATE_TOKENS);
    for (const row of this.#elsScheduleRows) {
      toggleChildState(row, STATES.ACTIVE, false);
    }
    super.unmount();
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DATA READING
   * ───────────────────────────────────────────────────────────────────── */

  #readStoreData() {
    const script = this.role(ROLES.DATA);

    if (!script) {
      throw new Error(`[store] Missing script[liftr-role="${ROLES.DATA}"]`);
    }

    let raw;
    try {
      raw = JSON.parse(script.textContent || '{}');
    } catch (err) {
      throw new Error(`[store] Invalid JSON in ${ROLES.DATA}: ${err.message}`);
    }

    const handle = raw?.handle;
    if (!handle) {
      throw new Error('[store] Missing handle in JSON data');
    }
    this.#storeId = String(handle);

    const regular = raw?.schedule?.regular;
    if (!regular) {
      throw new Error('[store] Missing schedule.regular in JSON data');
    }
    this.#schedule = { regular };

    this.#storeCoords = geo.extractStoreCoords(raw);
  }

  /* ─────────────────────────────────────────────────────────────────────
   * ELEMENT QUERIES
   * ───────────────────────────────────────────────────────────────────── */

  #queryElements() {
    // Opening elements
    this.#elsOpeningLabel = this.roleAll(ROLES.LIVE_CURRENT);
    this.#elsOpeningNext = this.roleAll(ROLES.LIVE_NEXT);

    // Distance elements
    this.#elsLabelDistance = this.roleAll(ROLES.LABEL_DISTANCE);
    this.#elsLabelWalking   = this.roleAll(ROLES.LABEL_WALKING);
    this.#elsLabelBicycling = this.roleAll(ROLES.LABEL_BICYCLING);
    this.#elsLabelDriving   = this.roleAll(ROLES.LABEL_DRIVING);
    this.#elLinkWalking     = this.role(ROLES.LINK_WALKING);
    this.#elLinkBicycling   = this.role(ROLES.LINK_BICYCLING);
    this.#elLinkDriving     = this.role(ROLES.LINK_DRIVING);
    this.#elsDirectionsLink = this.roleAll(ROLES.LINK_DIRECTION);
    this.#directionsHrefFallbacks = this.#elsDirectionsLink.map(el => el.getAttribute('href') || '');

    // Schedule table rows
    this.#elsScheduleRows = this.roleAll(ROLES.SCHEDULE_ROW);

    // List filter fields
    this.#elFieldDistanceKm   = this.querySelector(`[${ATTRIBUTES.FIELD}="${FIELDS.DISTANCE_KM}"]`);
    this.#elFieldDistanceM    = this.querySelector(`[${ATTRIBUTES.FIELD}="${FIELDS.DISTANCE_M}"]`);
    this.#elFieldDistanceNear = this.querySelector(`[${ATTRIBUTES.FIELD}="${FIELDS.DISTANCE_NEAR}"]`);
    this.#elFieldOpening      = this.querySelector(`[${ATTRIBUTES.FIELD}="${FIELDS.OPENING}"]`);
  }

  /* ─────────────────────────────────────────────────────────────────────
   * OPENING HOURS SETUP
   * ───────────────────────────────────────────────────────────────────── */

  #setupOpening() {
    const timezone = settings.market?.timezone || 'Europe/Berlin';
    this.#marketTz = timezone;

    const rawLabels = i18n.get('live');
    const labels = live.transformLiveLabels(rawLabels);

    if (!labels) {
      throw new Error('[store] i18n.live translations missing');
    }

    this.#closingThreshold = this.config.closingThresholdMin || 30;
    live.setClosingThreshold(this.#closingThreshold);

    this.#dateFx = live.createDateFx({ timezone });

    // this.#schedule.regular ist entweder das Legacy-Eigenformat ({mon..sun})
    // oder das Google-Composite ({regular,current}). createOpeningCore erkennt
    // die Shape selbst und normalisiert das Composite intern (inkl. per-Store
    // overrideByISO). Feiertage kommen bei Stores ausschließlich aus Google —
    // keinen store-seitigen Holiday-Override mehr durchreichen. Payload bleibt
    // roh — keine Transformation im Liquid.
    this.#openingCore = live.createOpeningCore({
      dateFx: this.#dateFx,
      regular: this.#schedule.regular
    });

    this.#openingFormatter = live.createOpeningFormatter({
      labels,
      dateFx: this.#dateFx,
      closingThresholdMin: this.#closingThreshold
    });

    const computeStatus = (now) => {
      const status = this.#openingCore.currentStatus(new Date(now), this.#closingThreshold);
      return {
        id: this.#storeId,
        status: status.status || live.STATUS.UNKNOWN,
        closesMin: status.closesMin ?? null,
        nextMin: status.nextMin ?? null,
        nextStep: status.nextStep ?? 0,
        nextDelta: status.nextDelta ?? null
      };
    };

    const unregister = live.registerStore({
      id: this.#storeId,
      compute: computeStatus
    });
    this.addDisposable(unregister);

    const unsubMinute = live.onEveryMinute(() => {
      this.#renderOpening();
      this.#renderScheduleDay();
    });
    this.addDisposable(unsubMinute);

    const handlePageShow = (e) => {
      if (e?.persisted) this.#renderOpening();
    };
    window.addEventListener('pageshow', handlePageShow);
    this.addDisposable(() => window.removeEventListener('pageshow', handlePageShow));
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DISTANCE SETUP
   * ───────────────────────────────────────────────────────────────────── */

  #setupDistance() {
    const cfg = this.config;
    this.#thresholds = { ...LiftrStore.DEFAULTS.thresholds, ...(cfg.thresholds || {}) };
    this.#timeFormat = { ...LiftrStore.DEFAULTS.timeFormat, ...(cfg.timeFormat || {}) };
    this.#displayUnits = { ...LiftrStore.DEFAULTS.displayUnits, ...(cfg.displayUnits || {}) };

    if (!this.#storeCoords) return;

    // Single listener — liftr-location handles all input orchestration
    this.addDisposable(
      on(LISTEN.LOCATION_RESOLVED, (e) => this.#onLocationResolved(e.detail))
    );

    // Initial distance state
    this.state.add(STATES.DISTANCE_UNSET);
  }

  /* ─────────────────────────────────────────────────────────────────────
   * OPENING RENDER
   * ───────────────────────────────────────────────────────────────────── */

  #renderOpening() {
    const status = this.#openingCore.currentStatus(new Date(), this.#closingThreshold);
    const statusStr = status.status || live.STATUS.UNKNOWN;

    const newStates = resolveOpeningStates(statusStr);

    const statesKey = newStates.join(',');
    const lastStatesKey = this.#lastOpening.states.join(',');

    if (statesKey !== lastStatesKey) {
      this.state.remove(...OPENING_STATE_TOKENS);

      for (const s of newStates) {
        this.state.add(s);
      }

      this.#lastOpening.states = newStates;

      if (this.#elFieldOpening) {
        const valueStr = newStates.join(' ');
        this.#elFieldOpening.textContent = valueStr;
        this.#elFieldOpening.setAttribute(ATTRIBUTES.VALUE, valueStr);
      }

      this.emit(EVENTS.OPENING_CHANGED, {
        id: this.#storeId,
        status: statusStr,
        states: newStates
      });
    }

    const { current, next } = this.#openingFormatter.compose(status);
    const currentText = current || '';
    const nextText = next || '';

    if (this.#lastOpening.current !== currentText) {
      this.#elsOpeningLabel.forEach(el => el.textContent = currentText);
      this.#lastOpening.current = currentText;
    }

    if (this.#lastOpening.next !== nextText) {
      this.#elsOpeningNext.forEach(el => el.textContent = nextText);
      this.#lastOpening.next = nextText;
    }
  }

  #neutralizeOpening() {
    this.state.remove(...OPENING_STATE_TOKENS);

    this.#elsOpeningLabel.forEach(el => el.textContent = '');
    this.#elsOpeningNext.forEach(el => el.textContent = '');
    if (this.#elFieldOpening) {
      this.#elFieldOpening.textContent = '';
      this.#elFieldOpening.setAttribute(ATTRIBUTES.VALUE, '');
    }
    this.#lastOpening = { states: [], current: null, next: null };
  }

  /* ─────────────────────────────────────────────────────────────────────
   * SCHEDULE WEEK RENDER (once, on mount)
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Fills the 7 pre-rendered rows with their opening-hours slots from
   * core.displayWeek() (regular, without ISO overrides) via formatter.week().
   * The Liquid snippet only ships the static skeleton (empty times container);
   * this keeps the day cells out of the CDN page cache so midnight crossover
   * and per-store hours are always correct.
   *
   * The produced DOM is byte-identical to the former SSR output:
   *   - closed → <span class="text-muted">{closedLabel}</span>
   *   - open   → <span liftr-role="store-schedule-slot"
   *                    liftr-value="HH:MM|HH:MM">HH:MM – HH:MM</span>
   */
  #renderScheduleWeek() {
    if (!this.#elsScheduleRows.length) return;

    const week = this.#openingFormatter.week(this.#openingCore.displayWeek());

    for (const row of this.#elsScheduleRows) {
      const dayKey = row.getAttribute(ATTRIBUTES.VALUE);
      const entry = week[dayKey];
      const container = row.querySelector('.store-panel_opening-times');
      if (!entry || !container) continue;

      const frag = document.createDocumentFragment();
      for (const slot of entry.slots) {
        const span = document.createElement('span');
        if (slot.muted) {
          span.className = 'text-muted';
        } else {
          span.setAttribute(ATTRIBUTES.ROLE, ROLES.SCHEDULE_SLOT);
          span.setAttribute(ATTRIBUTES.VALUE, slot.value);
        }
        span.textContent = slot.text;
        frag.appendChild(span);
      }
      container.replaceChildren(frag);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
   * SCHEDULE DAY RENDER (per minute)
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Marks the current weekday row with liftr-state="active" and, within it,
   * the currently running slot. Source of truth is core.spansForDate(today,
   * { includeCarry:false }) — the day's own spans in today's coordinates, NOT
   * a re-parse of the DOM. Slots are index-aligned to the spans rendered by
   * #renderScheduleWeek (same regular basis, same order).
   *
   * A window running over from the previous day is a carry (excluded here), so
   * it marks the calendar row active but no slot — the live label above still
   * shows "open / closes at …". Called on mount and every minute.
   */
  #renderScheduleDay() {
    if (!this.#elsScheduleRows.length) return;
    const now = new Date();
    const todayKey = live.weekdayKey(this.#dateFx, now);
    const { mins } = this.#dateFx.nowInfo(now);
    const todaySpans = this.#openingCore.spansForDate(now, { includeCarry: false });

    for (const row of this.#elsScheduleRows) {
      const isActiveRow = row.getAttribute(ATTRIBUTES.VALUE) === todayKey;
      toggleChildState(row, STATES.ACTIVE, isActiveRow);

      const slots = row.querySelectorAll(`[liftr-role~="${ROLES.SCHEDULE_SLOT}"]`);
      slots.forEach((slot, i) => {
        let isActiveSlot = false;
        if (isActiveRow) {
          const span = todaySpans[i];
          if (span) isActiveSlot = mins >= span[0] && mins < span[1];
        }
        toggleChildState(slot, STATES.ACTIVE, isActiveSlot);
      });
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DISTANCE — EVENT HANDLER
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Handle resolved location from liftr-location module.
   * Single entry point for all distance updates (PLZ + geolocation).
   * @param {{ valid: boolean, lat?: number, lng?: number, code?: string, source?: string, reason?: string }} detail
   */
  #onLocationResolved(detail) {
    if (!this.#storeCoords) return;

    if (!detail?.valid) {
      detail?.reason === 'cleared'
        ? this.#resetDistance()
        : this.#neutralizeDistance();
      return;
    }

    const result = geo.calculateDistanceFromCoords(
      this.#storeCoords,
      { lat: detail.lat, lng: detail.lng },
      { thresholds: this.#thresholds }
    );

    if (!result?.valid) return;

    this.#renderDistanceResult(result);
    this.#emitDistanceUpdate({
      valid: true,
      code: detail.code,
      km: result.km,
      meters: result.meters,
      category: result.category,
      source: detail.source
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DISTANCE RENDER — SHARED PATH
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Render a valid distance result.
   * @param {{ valid: boolean, km: number, meters: number, category: string, coords: LatLng }} result
   */
  #renderDistanceResult(result) {
    if (!result?.valid) return;

    const { km, meters, category, coords: targetCoords } = result;

    // Update native list filter fields
    const roundedKm = Math.max(0, Math.round(km));
    this.#updateFilterField(this.#elFieldDistanceKm, roundedKm);
    this.#updateFilterField(this.#elFieldDistanceM, meters);
    this.#updateFilterField(this.#elFieldDistanceNear, category === 'near' ? 'true' : '');

    // Format display value
    const { value: uiValue, unit: uiUnit } = geo.formatDistance(km, this.#displayUnits);

    // Update value/unit display
    this.#elsLabelDistance.forEach(el => el.textContent = `${uiValue} ${uiUnit}`);
    this.#elsLabelUnit.forEach(el => el.textContent = uiUnit);

    // Map category to state
    const distanceState = CATEGORY_TO_STATE[category] || STATES.DISTANCE_UNKNOWN;
    this.#setDistanceState(distanceState);

    // Update travel time labels
    const writeTime = (el, mins) => {
      if (!el) return;
      el.textContent = geo.formatDuration(mins, this.#timeFormat);
    };

    this.#elsLabelWalking.forEach(el   => writeTime(el, geo.estimateMins('walking', km, this.#marketTz)));
    this.#elsLabelBicycling.forEach(el => writeTime(el, geo.estimateMins('bicycling', km, this.#marketTz)));
    this.#elsLabelDriving.forEach(el   => writeTime(el, geo.estimateMins('driving', km, this.#marketTz)));

    // Update maps links
    if (targetCoords) {
      if (this.#elLinkWalking) {
        this.#elLinkWalking.href = geo.buildMapsUrl('walking', this.#storeCoords, targetCoords);
        this.#elLinkWalking.target = '_blank';
        this.#elLinkWalking.rel = 'noopener';
      }
      if (this.#elLinkBicycling) {
        this.#elLinkBicycling.href = geo.buildMapsUrl('bicycling', this.#storeCoords, targetCoords);
        this.#elLinkBicycling.target = '_blank';
        this.#elLinkBicycling.rel = 'noopener';
      }
      if (this.#elLinkDriving) {
        this.#elLinkDriving.href = geo.buildMapsUrl('driving', this.#storeCoords, targetCoords);
        this.#elLinkDriving.target = '_blank';
        this.#elLinkDriving.rel = 'noopener';
      }
    }

    // Update directions links (user coords as origin, store as destination)
    if (this.#storeCoords && targetCoords) {
      const travelmode = CATEGORY_TO_TRAVELMODE[category] || 'driving';
      const href = buildDirectionsUrl(targetCoords, this.#storeCoords, travelmode);
      for (const el of this.#elsDirectionsLink) {
        el.href = href;
      }
    }

    this.#lastDistance = { label: `${uiValue} ${uiUnit}`, category };
  }

  /* ─────────────────────────────────────────────────────────────────────
   * DISTANCE — STATE HELPERS
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Sets distance state (exclusive - only one at a time).
   * @param {string} state - Full state token from STATES
   */
  #setDistanceState(state) {
    this.state.remove(...DISTANCE_STATE_TOKENS);
    this.state.add(state);
  }

  /**
   * Update list filter field with value.
   * @param {HTMLElement|null} el - Field element
   * @param {string|number} value - Value to set
   */
  #updateFilterField(el, value) {
    if (!el) return;
    const strValue = String(value);
    el.textContent = strValue;
    el.setAttribute(ATTRIBUTES.VALUE, strValue);
  }

  /**
   * Restore Liquid-rendered fallback href on each directions link.
   * Fallback is the Google Place ID URL or address search URL set by Liquid.
   * Falls back to removing href if no Liquid fallback was present on mount.
   */
  #restoreDirectionsFallbacks() {
    this.#elsDirectionsLink.forEach((el, i) => {
      const fallback = this.#directionsHrefFallbacks[i];
      if (fallback) el.href = fallback;
      else el.removeAttribute('href');
    });
  }

  /**
   * Reset to initial state (no location entered).
   * State: distance-unset
   */
  #resetDistance() {
    this.#setDistanceState(STATES.DISTANCE_UNSET);

    // Clear display elements
    this.#elsLabelDistance.forEach(el => el.textContent = '');
    this.#elsLabelWalking.forEach(el   => el.textContent = '');
    this.#elsLabelBicycling.forEach(el => el.textContent = '');
    this.#elsLabelDriving.forEach(el   => el.textContent = '');

    // Reset native list filter fields to sentinel
    this.#updateFilterField(this.#elFieldDistanceKm, SORT_SENTINEL);
    this.#updateFilterField(this.#elFieldDistanceM, SORT_SENTINEL);
    this.#updateFilterField(this.#elFieldDistanceNear, '');

    // Remove maps link hrefs
    if (this.#elLinkWalking) this.#elLinkWalking.removeAttribute('href');
    if (this.#elLinkBicycling) this.#elLinkBicycling.removeAttribute('href');
    if (this.#elLinkDriving) this.#elLinkDriving.removeAttribute('href');

    // Restore directions links to Liquid fallback (Place ID or address)
    this.#restoreDirectionsFallbacks();

    this.#lastDistance = { label: null, category: null };

    this.#emitDistanceUpdate({
      valid: false,
      reason: 'unset',
      code: null
    });
  }

  /**
   * Set unknown state (PLZ not found/invalid).
   * State: distance-unknown
   */
  #neutralizeDistance() {
    this.#setDistanceState(STATES.DISTANCE_UNKNOWN);

    // Clear display elements
    this.#elsLabelDistance.forEach(el => el.textContent = '');
    this.#elsLabelWalking.forEach(el   => el.textContent = '');
    this.#elsLabelBicycling.forEach(el => el.textContent = '');
    this.#elsLabelDriving.forEach(el   => el.textContent = '');

    // Reset native list filter fields to sentinel
    this.#updateFilterField(this.#elFieldDistanceKm, SORT_SENTINEL);
    this.#updateFilterField(this.#elFieldDistanceM, SORT_SENTINEL);
    this.#updateFilterField(this.#elFieldDistanceNear, '');

    // Remove maps link hrefs
    if (this.#elLinkWalking) this.#elLinkWalking.removeAttribute('href');
    if (this.#elLinkBicycling) this.#elLinkBicycling.removeAttribute('href');
    if (this.#elLinkDriving) this.#elLinkDriving.removeAttribute('href');

    // Restore directions links to Liquid fallback (Place ID or address)
    this.#restoreDirectionsFallbacks();

    this.#lastDistance = { label: null, category: null };

    this.#emitDistanceUpdate({
      valid: false,
      reason: 'unknown',
      code: null
    });
  }

  #emitDistanceUpdate(detail) {
    this.emit(EVENTS.DISTANCE_CHANGED, {
      id: this.#storeId,
      ...detail
    });
  }

  /* ─────────────────────────────────────────────────────────────────────
   * PUBLIC API
   * ───────────────────────────────────────────────────────────────────── */

  /**
   * Get current opening status.
   * @returns {{ status: string, closesMin?: number, nextMin?: number }}
   */
  getOpeningStatus() {
    return this.#openingCore?.currentStatus(new Date(), this.#closingThreshold) ?? null;
  }

  /**
   * Get store ID (handle).
   * @returns {string|null}
   */
  getStoreId() {
    return this.#storeId;
  }
}

/* ═══════════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════════ */

customElements.define('liftr-store', LiftrStore);

export { LiftrStore, ROLES, STATES, EVENTS, SETTINGS };